import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, User, DollarSign, Calendar, Save, MessageSquare } from 'lucide-react';

const DonationFormModal = ({ isOpen, onClose, onSave, donationToEdit, members }) => {
  const [formData, setFormData] = useState({
    donorId: '',
    donorName: '',
    amount: '',
    donationDate: new Date().toISOString().slice(0, 10),
    paymentMethod: 'wechat',
    notes: ''
  });

  useEffect(() => {
    if (donationToEdit) {
      setFormData(donationToEdit);
    } else {
      setFormData({
        donorId: '',
        donorName: '',
        amount: '',
        donationDate: new Date().toISOString().slice(0, 10),
        paymentMethod: 'wechat',
        notes: ''
      });
    }
  }, [donationToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'donorId') {
      const selectedMember = members.find(m => m.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        donorName: selectedMember ? selectedMember.name : ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl p-4 sm:p-8 shadow-2xl w-full max-w-xs sm:max-w-lg md:max-w-2xl relative"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {donationToEdit ? 'Editar Donación' : 'Registrar Nueva Donación'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Donante (Socio)</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <Select
                    options={members.map(member => ({
                      value: member.id,
                      label: member.rut ? `${member.name} (${member.rut})` : member.name
                    }))}
                    placeholder="Buscar y seleccionar socio..."
                    isClearable
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        paddingLeft: '2.5rem',
                        minHeight: '3rem',
                        borderRadius: '0.75rem',
                        borderColor: '#e5e7eb',
                        boxShadow: 'none',
                        backgroundColor: '#f9fafb',
                        fontWeight: '500',
                        fontSize: '1rem',
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: '1rem',
                        backgroundColor: state.isSelected ? '#e0e7ff' : state.isFocused ? '#f3f4f6' : '#fff',
                        color: '#1f2937',
                      }),
                    }}
                    value={members.length > 0 && formData.donorId ? {
                      value: formData.donorId,
                      label: (() => {
                        const member = members.find(m => m.id === parseInt(formData.donorId));
                        return member ? (member.rut ? `${member.name} (${member.rut})` : member.name) : '';
                      })()
                    } : null}
                    onChange={option => {
                      setFormData(prev => ({
                        ...prev,
                        donorId: option ? option.value : '',
                        donorName: option ? option.label.split(' (')[0] : ''
                      }));
                    }}
                  />
                </div>
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Donante (si no es socio)</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="donorName"
                    value={formData.donorName}
                    onChange={handleChange}
                    placeholder="Nombre del donante"
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                    required={!formData.donorId} // Requerido si no se selecciona un socio
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monto ($)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="Monto de la donación"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Donación</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      name="donationDate"
                      value={formData.donationDate}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                >
                  <option value="wechat">WeChat Pay</option>
                  <option value="alipay">Efectivo</option>
                  <option value="bank">Transferencia Bancaria</option>
                  <option value="cash">Efectivo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Notas adicionales sobre la donación"
                    rows="3"
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300 resize-none"
                  />
                </div>
              </div>

              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Save className="w-5 h-5" />
                Guardar Donación
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DonationFormModal;